import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import AcademySuggest from "@/components/AcademySuggest";

const ROWS = [
  {
    belt: "White Belt", img: "/academy-assets/class-white.jpg",
    alt: "White Belt class scene: two students meeting their energy team around a panel",
    topic: "Your energy team",
    example: "Meet the players: panels make DC power, inverters turn it into AC, batteries store it, and the grid backfills the rest. You'll practice the kW vs kWh difference until it's reflex — the first question every good customer asks.",
  },
  {
    belt: "Yellow Belt", img: "/academy-assets/class-yellow.jpg",
    alt: "Yellow Belt class scene: a student ninja explaining electricity with a glowing bulb",
    topic: "Electricity without the mystery",
    example: "Voltage, current and circuit thinking — with real examples like why a sunny roof can still trip a breaker you sized wrong, and how to match an AC service without claiming compatibility you can't verify.",
  },
  {
    belt: "Orange Belt", img: "/academy-assets/class-orange.jpg",
    alt: "Orange Belt class scene: reviewing inverters and equipment paperwork on site",
    topic: "Equipment, documents and warranties",
    example: "Inverter architectures, MPPT and racking — then the pro skill: reading an actual spec sheet before promising anything. You'll compare two datasheets and spot what most salespeople get wrong.",
  },
  {
    belt: "Green Belt", img: "/academy-assets/class-green.jpg",
    alt: "Green Belt class scene: a rooftop site check with safety in mind",
    topic: "Roofs, safety and paperwork",
    example: "Site qualification and roof planning, hazard recognition and stop-work judgment, plus permits and interconnection — the class where you learn that 'no' is a professional answer.",
  },
  {
    belt: "Blue Belt", img: "/academy-assets/class-blue.jpg",
    alt: "Blue Belt class scene: two students reading a commercial electric bill together",
    topic: "Reading a commercial bill like a sensei",
    example: "Line by line: load shape, demand charges and self-consumption — and how exports and tariffs change the math for a storefront versus a warehouse. Bring your own (redacted) bill to practice on.",
  },
  {
    belt: "Purple Belt", img: "/academy-assets/class-purple.jpg",
    alt: "Purple Belt class scene: learning the Tesla Powerwall with an instructor",
    topic: "Tesla Powerwall, properly",
    example: "The Powerwall family and backup architecture, runtime reserves and power limits, and operating it all in the Tesla app — the path to the Powerwall Product Knowledge Certificate.",
  },
  {
    belt: "Brown Belt", img: "/academy-assets/class-brown.jpg",
    alt: "Brown Belt class scene: working through estimates and cash-flow charts",
    topic: "Honest money math",
    example: "Production estimates with real uncertainty, cash-flow and incentive literacy with dated sources — you'll build a proposal that survives a skeptical CFO instead of one that dies at the first question.",
  },
  {
    belt: "Black Belt", img: "/academy-assets/class-black.jpg",
    alt: "Black Belt class scene: the capstone review and oral defense",
    topic: "The capstone and oral defense",
    example: "Integrate everything on the warehouse capstone — full assessment, verification and troubleshooting — then defend your work in an oral review before your Black Belt is issued.",
  },
];

export default function AcademyInside() {
  return (
    <>
      <Seo
        title="Inside the Solar Black Belt Academy | What every belt covers"
        description="A look inside the Solar Black Belt Academy: what each of the 8 belts covers — from your first energy lesson to the Black Belt capstone — with class scenes. Free, self-paced solar and Tesla Powerwall training from Coastal Energy."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">INSIDE THE CLASS</span>
          <h1 data-testid="academy-inside-heading">What each belt teaches.</h1>
          <p className="lead">
            Eight belts, eight worlds. Here's a brief look at what you'll actually cover in class — alternating
            scenes from the course, one topic at a time.
          </p>
        </div>
      </section>

      <section className="wrap academy-section" data-testid="academy-inside-rows" style={{ maxWidth: 1120 }}>
        {ROWS.map((row, i) => (
          <div key={row.belt} className="academy-inside-row" data-testid={`academy-inside-${row.belt.split(" ")[0].toLowerCase()}`}
            style={{ flexDirection: i % 2 === 0 ? "row" : "row-reverse" }}>
            <img src={row.img} alt={row.alt} loading="lazy" />
            <div className="academy-inside-copy">
              <span className="academy-belt-chip">{row.belt}</span>
              <h2>{row.topic}</h2>
              <p>{row.example}</p>
              <Link className="textbutton" to={`/academy/belts/${row.belt.split(" ")[0].toLowerCase()}`}>See the {row.belt} syllabus →</Link>
            </div>
          </div>
        ))}
      </section>

      <AcademySuggest page="/academy/inside" />

      <section className="academy-dark" data-testid="academy-inside-signup">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 30, padding: "56px 20px" }}>
          <img src="/academy-assets/ninja-stand.jpg" alt="Roi Solar pointing you to the sign-up button"
            style={{ width: 190, borderRadius: 20, boxShadow: "0 20px 44px rgba(0,0,0,.35)", transform: "rotate(-3deg)" }}
            data-testid="academy-inside-mascot" loading="lazy" />
          <div style={{ textAlign: "center" }}>
            <h2 style={{ color: "#fff", marginBottom: 8 }}>Ready to earn your White Belt?</h2>
            <p className="micro" style={{ color: "#d7e6e2", marginBottom: 20 }}>Free during program setup · self-paced · your belt, your pace.</p>
            <Link to="/academy/register" data-testid="academy-inside-signup-btn"
              style={{ display: "inline-block", background: "var(--lime,#c8f542)", color: "#113d40", fontWeight: 900, fontSize: "clamp(20px, 3.4vw, 34px)", padding: "26px 54px", borderRadius: 999, textDecoration: "none", boxShadow: "0 18px 40px rgba(200,245,66,.3)" }}>
              SIGN UP FOR THE CLASS ↗
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
